import * as vp from 'vopay';
import { Box, Card, Title, Tooltip, Pagination, usePagination, useUpdateQueryParams } from 'vopay';
import { FunctionComponent, useEffect, useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import moment from 'moment';
import ApprovalListSearchForm from './ApprovalListSearchForm';
import useFetchApprovalTransactions from '@/hooks/useApi/useFetchApprovalTransactions';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useTranslation } from 'react-i18next';

import { TableFiltersContainer, Table } from 'UI';

type QueryParams = {
  currency: string;
  searchTerm: string;
  startDate: string;
  endDate: string;
};

type ApprovalListSearchParams = {
  currency?: string;
  searchTerm?: string;
  timeRange?: [vp.Timestamp, vp.Timestamp];
};

type FC = FunctionComponent;
export type SelectableApprovalInboxTransaction = vp.ApprovalInboxTransaction & {
  id: string;
  isSelected: boolean;
  isDialogueVisible: boolean;
};
const ApprovalListContainer: FC = () => {
  const [approvalTransactions, setApprovalTransactions] = useState<SelectableApprovalInboxTransaction[]>([]);
  const [filteredList, setFilteredList] = useState<vp.ApprovalInboxTransaction[]>([]);
  const [showApproved, setShowApproved] = useState(false);
  const [showRecjected, setShowRejected] = useState(false);
  const [approvalTransactionInbox, , , fetchApprovalTansactions] = useFetchApprovalTransactions({});

  const [hasInvalidEndDate, setHasInvalidEndDate] = useState(false);
  const [shouldShowEndDateError, setShouldShowEndDateError] = useState(false);

  const location = useLocation();

  const queryParams: QueryParams = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      currency: params.get('currency') || '',
      searchTerm: params.get('searchTerm') || '',
      startDate: params.get('startDate') || '',
      endDate: params.get('endDate') || '',
    };
  }, [location.search]);
  const currency = useMemo(() => queryParams.currency || '', [queryParams]);
  const searchTerm = useMemo(() => queryParams.searchTerm || '', [queryParams]);
  const startDate = useMemo(() => queryParams.startDate || '', [queryParams]);
  const endDate = useMemo(() => queryParams.endDate || '', [queryParams]);
  const timeDifference = moment().subtract(1, 'year').utc().format('YYYY-MM-DD');
  const currentDateTime = moment().utc().format('YYYY-MM-DD');
  const filtersApplied = useMemo(() => {
    return Boolean(
      currency || searchTerm || (startDate && startDate !== timeDifference) || (endDate && endDate !== currentDateTime),
    );
  }, [currency, searchTerm, startDate, endDate, timeDifference, currentDateTime]);

  const { t } = useTranslation('translations', { keyPrefix: 'TransactionApproval.ApprovalList' });

  const updateQueryParams = useUpdateQueryParams<QueryParams>();

  const alertDialog = useAlertDialog();

  // pagination
  const totalDataLength = useMemo(() => {
    return approvalTransactionInbox?.PaginationMetaData?.TotalRecords || 0;
  }, [filteredList]);
  const PER_PAGE = 20;
  const { pageCount, pageIndex, setPageIndex } = usePagination(totalDataLength, {
    pageSize: PER_PAGE,
  });

  const search = async (offset = 0, searchParams: ApprovalListSearchParams = {}) => {
    const defaultValues = {
      StartDateTime: moment(searchParams.timeRange?.[0] ?? (startDate || timeDifference))
        .startOf('day')
        .format('YYYY-MM-DD HH:mm:ss'),
      EndDateTime: moment(searchParams.timeRange?.[1] ?? (endDate || currentDateTime))
        .endOf('day')
        .format('YYYY-MM-DD HH:mm:ss'),
      Currency: searchParams.currency ?? currency,
      SearchText: searchParams.searchTerm ?? searchTerm,
      Limit: PER_PAGE,
      Offset: offset,
    };

    if (hasInvalidEndDate) {
      setShouldShowEndDateError(true);
      return;
    }

    try {
      const res = await fetchApprovalTansactions(defaultValues);

      if (!res.Success) throw new Error(res.ErrorMessage);
      const approvalTransactionRequest = Object.values(res.Transactions || {});
      setFilteredList(approvalTransactionRequest);
    } catch (err) {
      const msg =
        err instanceof Error
          ? err.message
          : t('Search.fetchErrorMessage', { defaultValue: 'An error occurred while fetching approval transactions' });
      alertDialog.error('Error', msg);
      console.error(err);
    }
  };

  useEffect(() => {
    if (showApproved) {
      setShowApproved(false);
    }
  }, [showApproved]);
  useEffect(() => {
    if (showRecjected) {
      setShowRejected(false);
    }
  }, [showRecjected]);

  useEffect(() => {
    if (startDate > endDate) {
      setHasInvalidEndDate(true);
      setShouldShowEndDateError(true);
    } else {
      setHasInvalidEndDate(false);
    }
  }, [endDate]);

  useEffect(() => {
    const offset = setPageIndex(0);
    const searchParams = {
      currency,
      searchTerm,
      startDate: moment(startDate || timeDifference).format('YYYY-MM-DD'),
      endDate: moment(endDate || currentDateTime).format('YYYY-MM-DD'),
    };
    updateQueryParams(searchParams);
    search(offset, searchParams);
  }, []);

  useEffect(() => {
    setFilteredList(approvalTransactions || {});
  }, [approvalTransactions]);
  useEffect(() => {
    setApprovalTransactions(
      Object.values(approvalTransactionInbox?.Transactions || {}).map((transaction, index) => ({
        ...transaction,
        id: index.toString(), // Add a comma here
        isSelected: false,
        isDialogueVisible: false,
      })),
    );
  }, [approvalTransactionInbox?.Transactions]);
  const tableProps: vp.TableProps<vp.ApprovalInboxTransaction> = {
    id: 'scheduled-transactions-list',
    version: '2',
    zebra: true,
    columns: [
      {
        id: 'createdOn',
        header: t('Table.createdOn', { defaultValue: 'Created On' }),
        align: 'left',
        field: 'TransactionDate',
      },
      {
        id: 'transactionId',
        header: t('Table.transactionId', {
          defaultValue: 'Trans. ID',
        }),
        align: 'left',
        template: ({ data }) => (
          <Table.Link
            to={{
              pathname: `/transactions-history/details/${data.TransactionType}/${data.ID}/${data.Currency}/`,
            }}
          >
            {data.ID}
          </Table.Link>
        ),
      },
      {
        id: 'name',
        header: t('Table.name', { defaultValue: 'Name' }),
        align: 'left',
        field: 'FullName',
      },
      {
        id: 'amount',
        header: t('Table.amount', { defaultValue: 'Amount' }),
        align: 'left',
        template: ({ data }) => `$${vp.formattedCurrency(data.Amount.toString())} ${data.Currency}`,
      },
      {
        id: 'paymentRail',
        header: t('Table.paymentRail', { defaultValue: 'Payment Rail' }),
        align: 'left',
        field: 'TransactionType',
      },
      {
        id: 'numberOfAvailableApprovers',
        header: (
          <Box gap="sm">
            {`${t('Table.NumberOfAvailableApprovers.label', { defaultValue: 'Available Approvers' })} `}
            <Tooltip
              icon="info"
              position="top"
              type="hover"
              width="250px"
              information={t('Table.NumberOfAvailableApprovers.tooltip', {
                defaultValue: 'This is the number of approvers that have the ability to approve the transaction.',
              })}
            />
          </Box>
        ),
        align: 'left',
        template: ({ data }) => data.AvailableApprovers.toString(),
      },
      {
        id: 'numberOfApprovers',
        header: (
          <Box gap="sm">
            {`${t('Table.NumberOfApprovers.label', { defaultValue: 'Approvers Required' })} `}
            <Tooltip
              icon="info"
              position="top"
              type="hover"
              width="250px"
              information={t('Table.NumberOfApprovers.tooltip', {
                defaultValue: 'This is the total number of approvers that are required to approve this transaction.',
              })}
            />
          </Box>
        ),
        align: 'left',
        template: ({ data }) => data.NumberApprovalsRequired.toString(),
      },
      {
        id: 'numberApproved',
        header: (
          <Box gap="sm">
            {`${t('Table.NumberApproved.label', { defaultValue: 'Completed Approvals' })} `}
            <Tooltip
              icon="info"
              position="top"
              type="hover"
              width="250px"
              information={t('Table.NumberApproved.tooltip', {
                defaultValue: 'This is the number of approvers that have approved the transaction.',
              })}
            />
          </Box>
        ),
        align: 'left',
        template: ({ data }) => data.NumberApproved.toString(),
      },
      {
        id: 'error',
        header: '',
        align: 'right',
        template: ({ data }) =>
          data.NumberApprovalsRequired > data.AvailableApprovers ? (
            <Tooltip
              icon="info"
              position="left"
              type="hover"
              color="#d22d2d"
              bgColor="transparent"
              width="350px"
              iconColor="error"
              borderColor="#d22d2d"
              information={
                <Box flx="col" jc="fs">
                  <div style={{ textAlign: 'left' }}>
                    <Box flx="col" pb="sm">
                      {t('Table.ApprovalError.message', {
                        defaultValue:
                          'This transaction cannot be approved as number of approvers needed to approve this transaction does not match the available approvers.',
                      })}
                    </Box>
                    <a
                      href="/transaction-approval/configuration/roles"
                      style={{ color: '#3B339E', textDecoration: 'underline' }}
                    >
                      {t('Table.ApprovalError.linkLabel', { defaultValue: 'View Roles' })}
                    </a>
                  </div>
                </Box>
              }
            />
          ) : null,
      },
    ],
    rows: filteredList.map((transaction) => ({
      id: String(transaction.ID || transaction.FileName || Math.random()),
      data: transaction,
    })),
  };

  return (
    <Box flx="col" gapSize="36px">
      <Box jc="sb">
        <Title title={t('title', { defaultValue: 'Approval List' })} />
      </Box>
      <Card
        style={{
          padding: '16px',
        }}
      >
        <Box flx="col" gap="sm">
          <TableFiltersContainer filtersApplied={filtersApplied}>
            <TableFiltersContainer.Trigger />
            <TableFiltersContainer.Content>
              <ApprovalListSearchForm
                currency={currency}
                searchTerm={searchTerm}
                hasInvalidEndDate={hasInvalidEndDate}
                shouldShowEndDateError={shouldShowEndDateError}
                setShouldShowEndDateError={setShouldShowEndDateError}
                initialTimeRange={[
                  moment(startDate || timeDifference).valueOf() as vp.Timestamp,
                  moment(endDate || currentDateTime).valueOf() as vp.Timestamp,
                ]}
                search={(searchParams) => {
                  const offset = setPageIndex(0);
                  updateQueryParams({
                    currency: searchParams.currency || '',
                    searchTerm: searchParams.searchTerm || '',
                    startDate: moment(searchParams.timeRange?.[0]).format('YYYY-MM-DD'),
                    endDate: moment(searchParams.timeRange?.[1]).format('YYYY-MM-DD'),
                  });
                  search(offset, searchParams);
                }}
              />
            </TableFiltersContainer.Content>
          </TableFiltersContainer>
        </Box>
        <Box flx="col" position="relative">
          <Table {...tableProps} />
        </Box>
        <Box width="100%" jc="center" mt="lg" mb="sm">
          {pageCount > 1 && (
            <Pagination
              pageCount={pageCount}
              forcePageNumber={pageIndex}
              handlePageClick={(e) => {
                const offset = setPageIndex(e.selected);
                const searchParams = {
                  currency,
                  searchTerm,
                  startDate: moment(startDate || timeDifference).format('YYYY-MM-DD'),
                  endDate: moment(endDate || currentDateTime).format('YYYY-MM-DD'),
                };
                updateQueryParams(searchParams);
                search(offset, searchParams);
              }}
            />
          )}
        </Box>
      </Card>
    </Box>
  );
};
export default ApprovalListContainer;
